import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import isEqual from 'lodash/isEqual';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	JiraSpaRoute,
	RouterContext,
} from '@atlassian/react-resource-router/src/common/types.tsx';
import {
	addRouteListener,
	getRouterState,
} from '@atlassian/react-resource-router/src/controllers/router-store/index.tsx';
import type { RouterState } from '@atlassian/react-resource-router/src/controllers/router-store/types.tsx';
import type { SelectedPath, SelectedPathData } from '../../types.tsx';
import { appsConverter } from './route-converters/apps/index.tsx';
import { assetsConverter } from './route-converters/assets/index.tsx';
import { customerExperiencesConverter } from './route-converters/customer-experiences.tsx';
import { customerServiceConverter } from './route-converters/customer-service/index.tsx';
import { dashboardsConverter } from './route-converters/dashboards/index.tsx';
import { filtersConverter } from './route-converters/filters/index.tsx';
import { helpCenterManagementConverter } from './route-converters/helpCenterManagementConverter.tsx';
import { itamConverter } from './route-converters/itam/itamConverter.tsx';
import { operationsConverter } from './route-converters/operations/index.tsx';
import { topLevelConverter } from './route-converters/others/index.tsx';
import { plansConverter } from './route-converters/plans/index.tsx';
import { projectsConverter } from './route-converters/projects/index.tsx';
import { roadmapsConverter } from './route-converters/roadmaps/index.tsx';
import { serviceManagementProjectSettingsConverter } from './route-converters/service-management-project-settings/index.tsx';

/**
 * This function returns the path to the selected __menu item__ in the sidebar.
 * The sidebar has a tree structure. Knowing the routeContext and the sidebar's
 * component hierarchy, this function can determine what nodes in the sidebar
 * tree should be "selected" (if the route contains enough information in the URL).
 *
 * Each child-function (e.g. getFilters()) is responsible for determining the
 * selected path for a set of route names. Sometimes a route name, on it's own,
 * is not enough to determine which parent-path should be selected.
 *
 * However, to keep the overall logic simple, a route-name SHOULD APPEAR IN ONLY
 * ONE Set(). __It then becomes the responsibility for the child-function to handle
 * all the possible selected-paths that could occur in response to the route name.__
 *
 * To help ensure that the sets contain non-overlapping route names, the unit test will
 * check this.
 *
 * @deprecated This function is deprecated. Use useSelectedPath() instead.
 */
export function routeToSelectedPath(routeContext: RouterContext<JiraSpaRoute>): SelectedPath {
	const routeName = routeContext.route.name;

	if (topLevelConverter.routes.has(routeName)) {
		return topLevelConverter.convert(routeContext);
	}

	if (appsConverter.routes.has(routeName)) {
		return appsConverter.convert(routeContext);
	}

	if (assetsConverter.routes.has(routeName)) {
		return assetsConverter.convert(routeContext);
	}

	if (dashboardsConverter.routes.has(routeName)) {
		return dashboardsConverter.convert(routeContext);
	}

	if (filtersConverter.routes.has(routeName)) {
		return filtersConverter.convert(routeContext);
	}

	if (itamConverter.routes.has(routeName) && fg('itam_ftw')) {
		return itamConverter.convert(routeContext);
	}

	if (operationsConverter.routes.has(routeName)) {
		return operationsConverter.convert(routeContext);
	}

	if (plansConverter.routes.has(routeName)) {
		return plansConverter.convert(routeContext);
	}

	if (projectsConverter.routes.has(routeName)) {
		return projectsConverter.convert(routeContext);
	}

	if (roadmapsConverter.routes.has(routeName)) {
		return roadmapsConverter.convert(routeContext);
	}

	if (serviceManagementProjectSettingsConverter.routes.has(routeName)) {
		return serviceManagementProjectSettingsConverter.convert(routeContext);
	}

	if (customerServiceConverter.routes.has(routeName)) {
		return customerServiceConverter.convert(routeContext);
	}

	if (customerExperiencesConverter.routes.has(routeName)) {
		return customerExperiencesConverter.convert(routeContext);
	}

	if (
		helpCenterManagementConverter.routes.has(routeName) &&
		expVal('jira_sidebar_show_helpcenter', 'isEnabled', false)
	) {
		return helpCenterManagementConverter.convert(routeContext);
	}

	return [];
}

export function getSelectedPath(state: RouterState): SelectedPath {
	const routeName = state.route.name;
	const match = state.match;
	const query = state.query;

	if (topLevelConverter.routes.has(routeName)) {
		return topLevelConverter.convertNew({ routeName });
	}

	if (appsConverter.routes.has(routeName)) {
		return appsConverter.convertNew({ routeName, match, query });
	}

	if (assetsConverter.routes.has(routeName)) {
		return assetsConverter.convertNew({ routeName });
	}

	if (dashboardsConverter.routes.has(routeName)) {
		return dashboardsConverter.convertNew({ routeName, match, query });
	}

	if (filtersConverter.routes.has(routeName)) {
		return filtersConverter.convertNew({ routeName, match, query });
	}

	if (itamConverter.routes.has(routeName) && fg('itam_ftw')) {
		return itamConverter.convertNew({ routeName });
	}

	if (operationsConverter.routes.has(routeName)) {
		return operationsConverter.convertNew({ routeName, query });
	}

	if (plansConverter.routes.has(routeName)) {
		return plansConverter.convertNew({ routeName, match });
	}

	if (projectsConverter.routes.has(routeName)) {
		return projectsConverter.convertNew({ routeName, match, query });
	}

	if (roadmapsConverter.routes.has(routeName)) {
		return roadmapsConverter.convertNew({ routeName, match });
	}

	if (serviceManagementProjectSettingsConverter.routes.has(routeName)) {
		return serviceManagementProjectSettingsConverter.convertNew({ routeName });
	}

	if (customerServiceConverter.routes.has(routeName)) {
		return customerServiceConverter.convertNew({ routeName });
	}

	if (customerExperiencesConverter.routes.has(routeName)) {
		return customerExperiencesConverter.convertNew({ routeName, match });
	}

	if (
		helpCenterManagementConverter.routes.has(routeName) &&
		expVal('jira_sidebar_show_helpcenter', 'isEnabled', false)
	) {
		return helpCenterManagementConverter.convertNew({ routeName, match });
	}

	return [];
}

// Refactored and exported for testing
export function useSelectedPath(): [SelectedPathData, Dispatch<SetStateAction<SelectedPathData>>] {
	return useState<SelectedPathData>(() => {
		const selectedPath = getSelectedPath(getRouterState());
		return {
			initialSelectedPath: selectedPath,
			selectedPath,
		};
	});
}

/**
 * Improved implementation: subscribes once on mount and never re-subscribes.
 * Uses the functional `setSelectedPathData` updater form to read the latest state without
 * capturing it in the closure, and a ref to access `initialSelectedPath` stably.
 * This eliminates the re-subscription race window on every navigation.
 */
export function useRouteToSelectedPath(): SelectedPathData {
	const [selectedPathData, setSelectedPathData] = useSelectedPath();

	useEffect(
		() =>
			addRouteListener((state) => {
				const nextSelectedPath = getSelectedPath(state);
				setSelectedPathData((prev) => {
					if (isEqual(nextSelectedPath, prev.selectedPath)) {
						return prev;
					}
					return {
						initialSelectedPath: prev.initialSelectedPath,
						selectedPath: nextSelectedPath,
					};
				});
			}),
		// addRouteListener is a stable module-level function and setSelectedPathData
		// is the stable React useState setter — neither ever changes, so this effect
		// runs exactly once on mount and unsubscribes on unmount.
		[setSelectedPathData],
	);

	return selectedPathData;
}
